import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Heart, Share2, ShoppingCart, Star, Users, Calendar, Eye, Package, Sparkles, Tag, ExternalLink, Globe, Instagram, X, Wallet, ArrowLeftRight, Image as ImageIcon, Box, ChevronRight } from 'lucide-react';
import ModelViewer from '../components/ModelViewer';
import UnifiedPieceCard from '../components/UnifiedPieceCard';
import PieceActivityAndHolders from '../components/PieceActivityAndHolders';
import { useWallet } from '../contexts/WalletContext';
import { useActivity } from '../contexts/ActivityContext';
import { useSolPrice } from '../contexts/SolPriceContext';
import { useFavorites } from '../hooks/useFavorites';
import { useCapsuleFavorites } from '../hooks/useCapsuleFavorites';
import { useWalletAction } from '../hooks/useWalletAction';
import { calculateSaleFeeWithUSD, logSaleFeeTransaction } from '../utils/saleFeeUtils';
import { getCapsuleById } from '../utils/supabaseDatabase';
import { normalizePieces } from '../utils/pieceNormalizer';
import { createGroupedPieceCards, isGroupedCard, getGroupLabel } from '../utils/pieceGrouping';
import { completePurchaseWithDelegate, checkSufficientBalance } from '../utils/marketplaceTransactions';
import { purchasePiece } from '../utils/editionPurchase';
import GroupedPiecesModal from '../components/GroupedPiecesModal';
import { trackCapsuleView } from '../utils/capsuleViewTracking';
import VerifiedBadge from '../components/VerifiedBadge';
import { createOffer } from '../utils/offersContract';
import RequestTradeModal from '../components/RequestTradeModal';

const CapsulePage = () => {
  const { capsuleId } = useParams();
  const navigate = useNavigate();
  const { isConnected, balance, publicKey, connectWallet, wallet } = useWallet();
  const { logSaleFeeCollected } = useActivity();
  const { solPrice } = useSolPrice(); // Get real-time SOL price from global context
  const { toggleFavorite, isFavorited } = useFavorites();
  const { toggleCapsuleFavorite, isCapsuleFavorited } = useCapsuleFavorites();
  const { withWalletCheck } = useWalletAction();
  const [capsule, setCapsule] = useState(null);
  const [creator, setCreator] = useState(null);
  const [relatedCapsules, setRelatedCapsules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedPiecesModal, setGroupedPiecesModal] = useState({ isOpen: false, groupedCard: null });
  const [pieceDetailModal, setPieceDetailModal] = useState({ isOpen: false, item: null });
  const [modalViewMode, setModalViewMode] = useState('3d'); // '3d' or 'thumbnail'
  const [activityPanelOpen, setActivityPanelOpen] = useState(false); // For sliding activity panel
  const [buyModal, setBuyModal] = useState({ isOpen: false, item: null });
  const [offerModal, setOfferModal] = useState({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
  const [requestTradeModal, setRequestTradeModal] = useState({ isOpen: false, item: null });
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  // Track which capsule was last viewed to prevent double-tracking
  const lastTrackedCapsule = React.useRef(null);

  useEffect(() => {
    loadCapsuleData();
  }, [capsuleId]);

  const loadCapsuleData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading capsule data for ID:', capsuleId);
      
      // Track capsule view (only if we haven't tracked this capsule in this session)
      if (capsuleId && lastTrackedCapsule.current !== capsuleId) {
        const walletAddress = publicKey?.toString() || null;
        const result = await trackCapsuleView(capsuleId, walletAddress);
        if (result.success) {
          console.log('✅ Capsule view tracked:', result.stats);
          lastTrackedCapsule.current = capsuleId;
        } else {
          console.log('⏭️ Capsule view not tracked:', result.error);
          // Still mark as tracked to avoid repeated failed attempts
          lastTrackedCapsule.current = capsuleId;
        }
      }
      
      // Load capsule from Supabase
      const foundCapsule = await getCapsuleById(capsuleId);
      
      if (!foundCapsule) {
        console.log('❌ Capsule not found, using mock data');
        // Fallback to mock data if not found
        const mockCapsule = {
          id: capsuleId || 'demo-capsule-1',
          name: 'Cyberpunk Streetwear Collection',
          description: 'A futuristic collection featuring cutting-edge digital fashion pieces perfect for the metaverse. Each piece combines bold cyberpunk aesthetics with premium 3D modeling techniques.',
          pieces: [
            {
              id: 'piece-1',
              name: 'Neon Jacket',
              modelName: 'Cyberpunk Jacket v2.1',
              category: 'Tops',
              modelColor: 'Electric Blue',
              price: '3.2 SOL',
              model_url: null,
              type: 'model/gltf-binary'
            },
            {
              id: 'piece-2',
              name: 'Holographic Pants',
              modelName: 'Holo Pants Pro',
              category: 'Bottoms',
              modelColor: 'Holographic',
              price: '2.8 SOL',
              model_url: null,
              type: 'model/gltf-binary'
            }
          ],
          views: 2847,
          likes: 156,
          downloads: 89,
          created_at: new Date().toISOString()
        };
        
        setCapsule(mockCapsule);
        setCreator(mockCapsule.creators || { wallet: 'demo-creator', handle: 'demo_creator' });
        setLoading(false);
        return;
      }
      
      console.log('✅ Capsule loaded from Supabase:', foundCapsule);
      
      // Normalize the pieces using the same normalizer as Explore page
      const normalizedPieces = normalizePieces(foundCapsule.pieces || []);
      
      console.log('✅ Normalized pieces:', normalizedPieces);
      
      // Apply grouping logic to capsule pieces (same as Explore page)
      // This groups pieces with quantity > 1 into grouped cards with _groupPieces
      const groupedPieces = createGroupedPieceCards(normalizedPieces);
      
      console.log('✅ Grouped capsule pieces:', groupedPieces);
      
      const transformedCapsule = {
        ...foundCapsule,
        collectionModels: groupedPieces, // Use grouped pieces instead of normalized
        models: groupedPieces, // For backward compatibility
        pieces: groupedPieces
      };
      
      setCapsule(transformedCapsule);
      setCreator(foundCapsule.creators);
      setIsDraft(foundCapsule.status === 'draft');
      
    } catch (error) {
      console.error('❌ Error loading capsule:', error);
      // Fallback to mock data on error
      const mockCapsule = {
        id: capsuleId || 'demo-capsule-1',
        name: 'Error Loading Capsule',
        description: 'There was an error loading this capsule.',
        pieces: [],
        views: 0,
        likes: 0,
        downloads: 0,
        created_at: new Date().toISOString()
      };
      setCapsule(mockCapsule);
      setCreator({ wallet: 'error', handle: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleBuyPiece = (piece) => {
    console.log('Opening buy modal for piece:', piece);
    handleBuyNow(piece);
  };

  const handleFavorite = () => {
    if (!capsule) return;
    
    // Use the hook's toggle function with full capsule data
    toggleCapsuleFavorite({
      id: capsule.id,
      title: capsule.name,
      name: capsule.name,
      description: capsule.description,
      banner: capsule.banner,
      image: capsule.banner,
      creator: creator?.handle || creator?.name,
      pieceCount: capsule.collectionModels?.length || 0,
      avgPrice: capsule.price || 0,
      price: capsule.price || 0,
      dropDate: capsule.created_at,
      createdAt: capsule.created_at,
      capsuleNumber: capsule.capsuleNumber
    });
  };

  const handleShare = async () => {
    if (!capsule) return;
    
    const shareData = {
      title: capsule.name,
      text: `Check out this digital fashion capsule: ${capsule.name}`,
      url: window.location.href
    };
    
    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        console.log('Content shared successfully');
      } else {
        // Fallback for browsers that don't support Web Share API
        await navigator.clipboard.writeText(window.location.href);
        showNotification('Capsule link copied to clipboard!', 'success');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        // Final fallback - try to copy to clipboard
        try {
          await navigator.clipboard.writeText(window.location.href);
          showNotification('Capsule link copied to clipboard!', 'success');
        } catch (clipboardError) {
          console.error('Clipboard error:', clipboardError);
          showNotification('Unable to share. Please copy the URL manually.', 'error');
        }
      }
    }
  };

  const handleLikeCapsule = () => {
    console.log('Liking capsule');
  };

  const handleShareCapsule = () => {
    navigator.clipboard.writeText(window.location.href);
    showNotification('Capsule link copied to clipboard!', 'success');
  };

  const formatUSDPrice = (price) => {
    // Handle both string ("2.5 SOL") and number (2.5) formats
    let solAmount = 0;
    if (typeof price === 'string') {
      solAmount = parseFloat(price.replace(' SOL', '').replace('SOL', '').trim());
    } else if (typeof price === 'number') {
      solAmount = price;
    } else {
      solAmount = parseFloat(price) || 0;
    }
    const usdAmount = (solAmount * solPrice).toFixed(2);
    return `~$${usdAmount} USD`;
  };

  // Social links helper functions
  const getSocialIcon = (platform) => {
    switch (platform) {
      case 'website':
        return <Globe className="w-4 h-4" />;
      case 'x':
        return <span className="text-white font-bold text-sm">𝕏</span>;
      case 'instagram':
        return <Instagram className="w-4 h-4" />;
      default:
        return <Globe className="w-4 h-4" />;
    }
  };

  const getSocialLabel = (platform) => {
    switch (platform) {
      case 'website':
        return 'Website';
      case 'x':
        return 'X';
      case 'instagram':
        return 'Instagram';
      default:
        return platform;
    }
  };

  const formatSocialUrl = (url, platform) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  // Modal handlers
  const openPieceDetailModal = (item) => {
    console.log('🏷️ Opening piece modal with tags:', item.tags);
    
    // Check if this is a grouped card - if so, show grouped pieces modal instead
    if (isGroupedCard(item)) {
      console.log('🔗 Grouped card detected - showing grouped pieces modal');
      console.log(`  - Group size: ${item._groupSize}`);
      console.log(`  - Available: ${item._availableCount}`);
      setGroupedPiecesModal({ isOpen: true, groupedCard: item });
      return;
    }
    
    // Normalize the piece data to ensure verificationType is correctly mapped
    const normalizedItem = item ? normalizePieces([item])[0] : item;
    setPieceDetailModal({ isOpen: true, item: normalizedItem });
  };

  const closePieceDetailModal = () => {
    setPieceDetailModal({ isOpen: false, item: null });
  };

  // Handle toggle favorite
  const handleToggleFavorite = (item) => {
    toggleFavorite(item.id);
  };

  // Handle account button click - navigate to creator profile
  const handleAccountClick = (item) => {
    const creatorWallet = item?.originalCreator?.wallet || item?.creator?.wallet || item?.creator_wallet;
    if (creatorWallet) {
      navigate(`/creator/${creatorWallet}`);
    }
  };

  // Handle buy now button
  const handleBuyNow = withWalletCheck((item) => {
    setBuyModal({ isOpen: true, item });
  }, 'make a purchase');

  // Process purchase with wallet - REAL BLOCKCHAIN TRANSACTION
  const processPurchase = async () => {
    if (!isConnected || !publicKey) {
      showNotification('Wallet not connected', 'error');
      return;
    }

    const { item } = buyModal;
    const price = parseFloat(item.listing_price || item.price || '0');
    
    if (!price || price <= 0) {
      showNotification('Cannot purchase piece with invalid price. Please refresh and try again.', 'error');
      closeBuyModal();
      return;
    }
    
    // Check sufficient balance (with buffer for gas fees)
    const balanceCheck = await checkSufficientBalance({ connected: isConnected, publicKey }, price);
    if (!balanceCheck.sufficient) {
      showNotification(`Not enough SOL. You need ${balanceCheck.required.toFixed(2)} SOL but only have ${balanceCheck.balance.toFixed(2)} SOL`, 'error');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Execute the complete purchase flow on blockchain using edition-aware purchase
      console.log(`🛒 Purchasing ${item.name} for ${price} SOL...`);
      console.log(`🎨 Edition system: Auto-selecting available edition if multi-edition piece`);
      
      // Use edition-aware purchase (automatically selects available edition)
      const result = await purchasePiece(
        wallet,
        item,
        price
      );
      
      // Log edition info if available
      if (result.editionNumber && result.totalEditions) {
        console.log(`✅ Purchased edition ${result.editionNumber}/${result.totalEditions}`);
      }
      
      // Log sale fee activity
      logSaleFeeCollected({
        pieceId: item.id,
        pieceName: item.name,
        feeAmount: result.paymentTransaction?.feeBreakdown?.feeAmount || result.feeBreakdown?.feeAmount || 0,
        piecePrice: price,
        buyerWallet: publicKey.toString(),
        sellerWallet: item.holder_wallet || item.creators?.wallet || 'Unknown Seller',
        transactionHash: result.paymentTransaction?.signature || result.transactionSignature
      });
      
      // Show success notification
      showNotification(
        `Successfully purchased ${item.name} for ${price.toFixed(2)} SOL! 🎉`,
        'success'
      );
      
      // Close modal
      setBuyModal({ isOpen: false, item: null });
      
      // Reload page to show updated ownership
      window.location.reload();
      
    } catch (error) {
      console.error('❌ Purchase failed:', error);
      // Make error messages user-friendly
      let userMessage = 'Unable to complete purchase. Please try again.';
      if (error.message?.includes('insufficient')) {
        userMessage = 'Not enough SOL in your wallet to complete this purchase.';
      } else if (error.message?.includes('rejected') || error.message?.includes('declined')) {
        userMessage = 'Purchase cancelled. You declined the transaction.';
      } else if (error.message?.includes('delegate') || error.message?.includes('delegated')) {
        userMessage = 'Unable to complete purchase. The item may no longer be available.';
      } else if (error.message?.includes('network') || error.message?.includes('timeout')) {
        userMessage = 'Network error. Please check your connection and try again.';
      }
      showNotification(userMessage, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Close buy modal
  const closeBuyModal = () => {
    setBuyModal({ isOpen: false, item: null });
  };

  // Handle make offer button
  const handleMakeOffer = withWalletCheck(async (item) => {
    // Check if user already has an active offer on this piece
    try {
      const { getPieceOffers } = await import('../utils/offersContract');
      const offersResult = await getPieceOffers(item.mint_address, 'active');
      
      if (offersResult.success && offersResult.offers.length > 0) {
        // Check if any of the active offers are from this user
        const userOffer = offersResult.offers.find(
          offer => offer.offerer_wallet === publicKey?.toString()
        );
        
        if (userOffer) {
          showNotification(
            `You already have an active offer of ${userOffer.offer_price} SOL on this piece. Visit the Offers page to view or update it.`,
            'info'
          );
          return;
        }
      }
    } catch (error) {
      console.log('Could not check for existing offers:', error);
      // Continue anyway - the backend will catch duplicates
    }
    
    setOfferModal({ isOpen: true, item, offerAmount: '', expirationDays: 7 });
  }, 'make an offer');

  // Process offer with wallet
  const processOffer = async () => {
    if (!isConnected || !publicKey) {
      showNotification('Wallet not connected', 'error');
      return;
    }

    const { item, offerAmount, expirationDays } = offerModal;
    const offerPrice = parseFloat(offerAmount);
    
    if (isNaN(offerPrice) || offerPrice <= 0) {
      showNotification('Please enter a valid offer amount', 'error');
      return;
    }

    if (balance < offerPrice) {
      showNotification(`Insufficient balance. You need ${offerPrice} SOL but only have ${balance.toFixed(4)} SOL`, 'error');
      return;
    }

    // Validate required fields
    if (!item.id || !item.mint_address) {
      showNotification('This piece is not ready for offers yet. Please try again later.', 'error');
      return;
    }

    // Extract the real piece ID (remove 'supabase_' prefix if present)
    let realPieceId = item.id;
    if (typeof item.id === 'string' && item.id.startsWith('supabase_')) {
      realPieceId = parseInt(item.id.replace('supabase_', ''));
    }

    setIsProcessing(true);
    
    try {
      console.log(`📝 Creating offer for ${item.name} of ${offerPrice} SOL`);
      console.log(`Piece ID: ${item.id} -> ${realPieceId}`);
      console.log(`Expires in: ${expirationDays} days`);
      console.log(`From wallet: ${publicKey.toString()}`);
      
      // Create offer using the real database function
      const result = await createOffer(wallet, {
        pieceId: realPieceId,
        mintAddress: item.mint_address,
        pieceName: item.name,
        offerPrice: offerPrice,
        message: '', // Could add message field to modal if desired
        expirationDays: expirationDays,
      });
      
      if (!result.success) {
        // Check if it's a duplicate offer error
        if (result.error && result.error.includes('already have an active offer')) {
          showNotification(
            'You already have an active offer on this piece. Visit the Offers page to view or update it.',
            'error'
          );
          setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
          return;
        }
        throw new Error(result.error || 'Failed to create offer');
      }
      
      console.log('✅ Offer created successfully!');
      console.log('Offer ID:', result.offer.id);
      
      // Show success notification
      showNotification(`Offer of ${offerPrice} SOL submitted successfully! 🎉`, 'success');
      
      // Close modal
      setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
      
    } catch (error) {
      console.error('❌ Offer creation failed:', error);
      
      // Extract user-friendly error message
      let errorMessage = 'Unable to create offer. Please try again.';
      
      // Check if error message contains the duplicate offer info
      if (error.message && error.message.includes('already have an active offer')) {
        errorMessage = 'You already have an active offer on this piece. Visit the Offers page to view or update it.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showNotification(errorMessage, 'error');
      
      // Close modal if it's a duplicate offer error
      if (errorMessage.includes('already have an active offer')) {
        setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Close offer modal
  const closeOfferModal = () => {
    setOfferModal({ isOpen: false, item: null, offerAmount: '', expirationDays: 7 });
  };

  // Handle request trade button
  const handleRequestTrade = withWalletCheck((item) => {
    setRequestTradeModal({ isOpen: true, item });
  }, 'request a trade');

  // Handle trade request submission
  const handleTradeRequestSubmit = (tradeRequest) => {
    console.log('Trade request submitted:', tradeRequest);
    // Close both the trade modal and the piece detail modal
    setRequestTradeModal({ isOpen: false, item: null });
    setPieceDetailModal({ isOpen: false, item: null });
  };

  // Handle trade request success
  const handleTradeRequestSuccess = (message) => {
    showNotification(message, 'success');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Loading capsule...</div>
      </div>
    );
  }

  if (!capsule) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📦</div>
          <h1 className="text-2xl font-bold text-white mb-2">Capsule Not Found</h1>
          <p className="text-gray-400 mb-6">This capsule doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/explore')}
            className="px-6 py-3 bg-white hover:bg-gray-200 text-gray-900 rounded-xl font-medium transition-colors"
          >
            Back to Explore
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Banner Section */}
      <div className="relative">
        <div className="aspect-[1400/400] bg-black flex items-center justify-center overflow-hidden">
          {capsule.banner ? (
            <img 
              src={capsule.banner} 
              alt={capsule.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="text-center text-white">
              <div className="text-6xl mb-4">📦</div>
              <h1 className="text-3xl font-bold">{capsule.name}</h1>
            </div>
          )}
          
          {/* Action Buttons - Bottom Right */}
          {!isDraft && (
            <div className="absolute bottom-4 right-4 flex space-x-2 z-20">
              <button 
                onClick={() => handleFavorite()}
                className="p-2 rounded-lg transition-all duration-300 hover:scale-110 active:scale-95 shadow-lg relative z-10 bg-black/40 backdrop-blur-sm"
              >
                <Heart 
                  size={20} 
                  className={`${
                    isCapsuleFavorited(capsule?.id) 
                      ? 'text-red-500 fill-red-500' 
                      : 'text-white/70 hover:text-red-500'
                  } transition-all duration-300`} 
                />
              </button>
              <button 
                onClick={() => handleShare()}
                className="p-2 rounded-lg transition-all duration-300 hover:scale-110 active:scale-95 shadow-lg relative z-10 bg-black/40 backdrop-blur-sm"
              >
                <Share2 size={20} className="text-white" />
              </button>
            </div>
          )}
        </div>
        
        {/* Creator Info Overlay */}
        <div className="absolute bottom-2 left-4 right-4">
          <div className="bg-black/60 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center gap-4">
              <Link
                to={`/creator/${creator?.wallet || creator?.id}`}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                <img
                  src={creator?.profile_picture_url || '/images/piece-placeholder.png'}
                  alt={creator?.handle || 'Creator'}
                  className="w-12 h-12 rounded-full cursor-pointer object-cover flex-shrink-0"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-semibold">
                      {creator?.handle || 'Unknown Creator'}
                    </span>
                    {creator?.is_verified && (
                      <VerifiedBadge 
                        size="sm"
                        verificationType={creator?.verification_type || 'none'}
                      />
                    )}
                  </div>
                </div>
              </Link>
              
              {/* Social Links */}
              {(capsule?.website || capsule?.twitter || capsule?.discord || capsule?.instagram) && (
                <div className="flex gap-2 items-center">
                  {capsule?.website && (
                    <a
                      href={formatSocialUrl(capsule.website, 'website')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative flex items-center justify-center w-8 h-8 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-all duration-300 hover:scale-105"
                      title="Website"
                    >
                      <div className="flex items-center justify-center w-4 h-4 text-white/80 group-hover:text-white transition-colors duration-300">
                        {getSocialIcon('website')}
                      </div>
                    </a>
                  )}
                  {capsule?.twitter && (
                    <a
                      href={formatSocialUrl(capsule.twitter, 'x')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative flex items-center justify-center w-8 h-8 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-all duration-300 hover:scale-105"
                      title="X (Twitter)"
                    >
                      <div className="flex items-center justify-center w-4 h-4 text-white/80 group-hover:text-white transition-colors duration-300">
                        {getSocialIcon('x')}
                      </div>
                    </a>
                  )}
                  {capsule?.instagram && (
                    <a
                      href={formatSocialUrl(capsule.instagram, 'instagram')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative flex items-center justify-center w-8 h-8 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-all duration-300 hover:scale-105"
                      title="Instagram"
                    >
                      <div className="flex items-center justify-center w-4 h-4 text-white/80 group-hover:text-white transition-colors duration-300">
                        {getSocialIcon('instagram')}
                      </div>
                    </a>
                  )}
                  {capsule?.discord && (
                    <a
                      href={formatSocialUrl(capsule.discord, 'discord')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative flex items-center justify-center w-8 h-8 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-all duration-300 hover:scale-105"
                      title="Discord"
                    >
                      <div className="flex items-center justify-center w-4 h-4 text-white/80 group-hover:text-white transition-colors duration-300">
                        <Globe className="w-4 h-4" />
                      </div>
                    </a>
                  )}
                </div>
              )}
            </div>
            
            {/* Capsule Date */}
            <div className="mt-2">
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <Calendar size={16} className="text-gray-400" />
                <span>{isDraft ? 'Created' : 'Dropped'} {new Date(capsule?.dropped_at || capsule?.createdAt || new Date()).toLocaleDateString('en-US', { 
                  year: '2-digit', 
                  month: '2-digit', 
                  day: '2-digit' 
                })}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 py-8">
        {/* Capsule Name */}
        <div className="mb-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
              {capsule.name}
            </h1>
          </div>
        </div>

        {/* Description Section */}
        <div className="mb-8">
          <div className="max-w-4xl mx-auto">
            <p className="text-gray-300 leading-relaxed text-lg text-center">
              {capsule.description || `A carefully curated collection of ${capsule.collectionModels?.length || 0} digital fashion pieces. Each item in this capsule has been designed with attention to detail, combining modern aesthetics with cutting-edge 3D technology. Perfect for creating unique virtual outfits and expressing your personal style in the metaverse.`}
            </p>
          </div>
        </div>

        {/* Pieces Grid */}
        <div className="mb-8">
          {capsule.collectionModels && capsule.collectionModels.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-4">
              {capsule.collectionModels.map((piece, index) => {
                // Check if current user owns this piece
                const isOwner = isConnected && publicKey && 
                  piece?.holder_wallet === publicKey.toString();
                
                // Determine if piece is owned by creator (primary sale)
                const creatorWallet = piece?.creator_wallet || 
                                    piece?.creators?.wallet ||
                                    piece?.originalCreator?.wallet;
                const isOwnedByCreator = piece?.holder_wallet === creatorWallet;
                
                // Show buy button only if:
                // 1. Not a draft, AND
                // 2. User doesn't own it, AND
                // 3. (Piece is owned by creator OR piece is listed for resale)
                const canBuy = !isDraft && !isOwner && (isOwnedByCreator || piece?.listing_status === 'listed');
                
                // Show trade button only if not draft and user doesn't own it
                const canTrade = !isDraft && !isOwner;
                
                return (
                  <UnifiedPieceCard
                    key={piece.id || index}
                    item={piece}
                    onClick={openPieceDetailModal}
                    onBuy={canBuy ? handleBuyPiece : null}
                    onTrade={canTrade ? handleRequestTrade : null}
                    onFavorite={isDraft ? null : handleToggleFavorite}
                    solPrice={solPrice}
                    showBuyButton={canBuy}
                    showFavoriteButton={!isDraft}
                    showTradeButton={canTrade}
                    isFavorited={isDraft ? false : isFavorited(piece.id)}
                    buyButtonText="Buy now"
                    tradeButtonText="Trade"
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl">
              <Package size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No pieces found in this capsule</p>
            </div>
          )}
        </div>

        {/* Related Capsules */}
        {!isDraft && relatedCapsules.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-6">More from {creator?.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedCapsules.map((relatedCapsule) => (
                 <div
                   key={relatedCapsule.id}
                   className="group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20"
                 >
                   {/* Banner Image - Full width, no padding */}
                   <div className="relative aspect-[1400/400] bg-[#2a2a2a] flex items-center justify-center overflow-hidden">
                     {relatedCapsule.banner ? (
                       <img 
                         src={relatedCapsule.banner} 
                         alt={relatedCapsule.name}
                         className="w-full h-full object-cover"
                       />
                     ) : relatedCapsule.image ? (
                       <img 
                         src={relatedCapsule.image} 
                         alt={relatedCapsule.name}
                         className="w-full h-full object-cover"
                       />
                     ) : (
                       <div className="text-4xl opacity-30">📦</div>
                     )}
                     
                     {/* Status Tag - Overlay on banner */}
                     <div className="absolute top-3 left-3">
                       <span className="px-2 py-0.5 bg-white text-black text-xs rounded-full" style={{color: '#111827'}}>
                         Capsule
                       </span>
                     </div>
                   </div>

                   {/* Bottom Section with Info */}
                   <div className="p-3 space-y-2">
                     {/* Title with Number */}
                     <h3 className="text-white font-bold text-sm line-clamp-1">
                       {relatedCapsule.name} {relatedCapsule.capsuleNumber}
                     </h3>

                     {/* Description */}
                     <p className="text-gray-400 text-xs line-clamp-2">
                       {relatedCapsule.description || `A curated collection of ${relatedCapsule.pieceCount} digital fashion pieces with modern styling and premium quality.`}
                     </p>

                     {/* Stats Row */}
                     <div className="flex items-center justify-between text-xs text-gray-400">
                       <div className="flex items-center gap-3">
                         <span className="flex items-center gap-1">
                           <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                           {relatedCapsule.pieceCount} pieces
                         </span>
                       </div>
                     </div>

                     {/* Price Row */}
                     <div className="space-y-1">
                       <div className="flex items-center justify-between">
                         <div className="text-xs text-gray-400">Avg Price</div>
                         <div className="text-green-400 font-semibold text-sm">
                           {relatedCapsule.avgPrice || relatedCapsule.price || '0.0'} SOL
                         </div>
                       </div>
                       <div className="text-right text-xs text-gray-400">
                         {relatedCapsule.avgPrice ? formatUSDPrice(relatedCapsule.avgPrice) : formatUSDPrice(relatedCapsule.price)}
                       </div>
                     </div>

                     {/* Drop Date */}
                     <div className="text-xs text-gray-500">
                       Dropped {relatedCapsule.dropDate ? new Date(relatedCapsule.dropDate).toLocaleDateString('en-US', { 
                         year: '2-digit', 
                         month: '2-digit', 
                         day: '2-digit' 
                       }) : relatedCapsule.createdAt ? new Date(relatedCapsule.createdAt).toLocaleDateString('en-US', { 
                         year: '2-digit', 
                         month: '2-digit', 
                         day: '2-digit' 
                       }) : 'Recently'}
                     </div>
                   </div>
                   
                   {/* Heart Icon */}
                   <button
                     onClick={(e) => {
                       e.stopPropagation();
                       // Add favorite functionality here
                     }}
                     className="absolute top-3 right-3 p-1.5 rounded-lg backdrop-blur-sm border transition-all duration-300 hover:scale-110 active:scale-95 bg-black/30 border-red-500/20 hover:bg-red-500/10 hover:border-red-500/30"
                   >
                     <Heart 
                       size={14} 
                       className="text-red-500 transition-all duration-300" 
                     />
                   </button>
                   
                   {/* Hover View Capsule Button - Slides up from bottom */}
                   <div className="absolute bottom-0 left-0 right-0 transform translate-y-full group-hover:translate-y-0 transition-all duration-300 ease-out p-3">
                     <Link
                       to={`/capsule/${relatedCapsule.id}`}
                       className="block w-full"
                     >
                       <button className="w-full bg-white hover:bg-gray-200 text-gray-900 py-2 px-3 rounded-lg font-semibold transition-all duration-200 text-sm">
                         View Capsule
                       </button>
                     </Link>
                   </div>
                 </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Piece Detail Modal */}
      {pieceDetailModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closePieceDetailModal}
        >
          <div 
            className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
            style={{ overflow: 'visible' }}
          >
            <div className="flex flex-1 overflow-hidden rounded-2xl" style={{ position: 'relative', zIndex: 1 }}>
              {/* Left side - Thumbnail/3D Model */}
              <div className="w-1/2 p-6 overflow-y-auto">
                {/* 3D Model Toggle Buttons */}
                {(() => {
                  const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                  const has3DModel = normalizedPiece?.previewUrl && (normalizedPiece.previewUrl.includes('.glb') || normalizedPiece.previewUrl.includes('.gltf'));
                  
                  if (has3DModel) {
                    return (
                      <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
                        <button
                          onClick={() => setModalViewMode('3d')}
                          className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                            modalViewMode === '3d'
                              ? 'text-white bg-[#2a2a2a] shadow-lg' 
                              : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                          }`}
                        >
                          3D Model
                        </button>
                        <button
                          onClick={() => setModalViewMode('thumbnail')}
                          className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200  ${
                            modalViewMode === 'thumbnail'
                              ? 'text-white bg-[#2a2a2a] shadow-lg' 
                              : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                          }`}
                        >
                          Thumbnail
                        </button>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                <div className="rounded-xl flex items-center justify-center mb-1 overflow-hidden aspect-square">
                  {(() => {
                    const normalizedPiece = pieceDetailModal.item ? normalizePieces([pieceDetailModal.item])[0] : null;
                    
                    if (modalViewMode === '3d') {
                      // Try to get bucket/key info from the piece data
                      const modelBucket = pieceDetailModal.item?.preview_bucket || pieceDetailModal.item?.model_bucket;
                      const modelKey = pieceDetailModal.item?.preview_key || pieceDetailModal.item?.model_key;
                      
                      // Fallback to legacy URL if bucket/key not available
                      const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
                      
                      if (modelBucket && modelKey) {
                        const customOrientation = pieceDetailModal.item?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            modelBucket={modelBucket}
                            modelKey={modelKey}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                          />
                        );
                      } else if (fallbackSrc) {
                        const customOrientation = pieceDetailModal.item?.customOrientation || null;
                        
                        return (
                          <ModelViewer
                            src={fallbackSrc}
                            posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                            orientation={customOrientation}
                            className="w-full h-full modal"
                          />
                        );
                      } else {
                        return (
                          <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-xl">
                            <div className="text-center text-white p-6">
                              <div className="text-6xl mb-4">⚠️</div>
                              <div className="text-xl font-semibold mb-2">No 3D model available</div>
                              <div className="text-sm text-gray-400">This piece doesn't have a 3D model</div>
                            </div>
                          </div>
                        );
                      }
                    } else {
                      // Show thumbnail image
                      return pieceDetailModal.item?.thumbnailUrl ? (
                        <img 
                          src={pieceDetailModal.item.thumbnailUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                          onError={(e) => {
                            console.log('Modal thumbnail load error for:', pieceDetailModal.item.name, 'URL:', pieceDetailModal.item.thumbnailUrl);
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : pieceDetailModal.item?.thumbnail?.dataUrl ? (
                        <img 
                          src={pieceDetailModal.item.thumbnail.dataUrl} 
                          alt={pieceDetailModal.item.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="No preview available"
                          className="w-full h-full object-cover"
                        />
                      );
                    }
                  })()}
                </div>

                {/* Creator and Holder Section */}
                <div className="space-y-4 mb-6">
                  {/* Original Creator */}
                  <div>
                    <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                      Original Creator
                    </div>
                    <button 
                      onClick={() => handleAccountClick(pieceDetailModal.item)}
                      className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                        {(pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url) ? (
                          <img
                            src={pieceDetailModal.item?.originalCreator?.profilePicture || pieceDetailModal.item?.creator?.profilePicture || pieceDetailModal.item?.originalCreator?.profile_picture_url || pieceDetailModal.item?.creator?.profile_picture_url}
                            alt={pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator'}
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {(pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Creator').charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                            {pieceDetailModal.item?.originalCreator?.username || pieceDetailModal.item?.creator?.username || 'Original Creator'}
                          </div>
                          {(pieceDetailModal.item?.originalCreator?.verified || pieceDetailModal.item?.creator?.verified) && (
                            <VerifiedBadge 
                              size="sm"
                              verificationType={pieceDetailModal.item?.originalCreator?.verificationType || pieceDetailModal.item?.creator?.verificationType || 'none'}
                            />
                          )}
                        </div>
                        <div className="text-gray-400 text-xs mt-0.5">
                          {pieceDetailModal.item?.originalCreator?.piecesCount || pieceDetailModal.item?.creator?.piecesCount || '0'} pieces created
                        </div>
                      </div>
                      <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </div>
                    </button>

                    {/* Creator Royalty - Outside button */}
                    {pieceDetailModal.item?.royalty_bps !== undefined && pieceDetailModal.item?.royalty_bps !== null && (
                      <div className="mt-3 px-1">
                        <span className="inline-block px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-xs">
                          {(pieceDetailModal.item.royalty_bps / 100).toFixed(1)}% Creator Royalty
                        </span>
                      </div>
                    )}

                    {/* Social Links - Show piece-specific OR creator social links */}
                    {(() => {
                      // Get piece-specific social links first (filled in during piece creation)
                      let socialLinks = pieceDetailModal.item?.social_links || {};
                      let hasSocialLinks = socialLinks.website || socialLinks.twitter || socialLinks.instagram || socialLinks.discord;
                      
                      // Fallback to creator social links if piece doesn't have them
                      if (!hasSocialLinks) {
                        const creatorSocialLinks = pieceDetailModal.item?.creator?.social_links || 
                                                  pieceDetailModal.item?.originalCreator?.social_links || 
                                                  pieceDetailModal.item?.creators?.social_links || {};
                        socialLinks = creatorSocialLinks;
                        hasSocialLinks = socialLinks.website || socialLinks.twitter || socialLinks.instagram || socialLinks.discord;
                      }
                      
                      if (!hasSocialLinks) return null;
                      
                      return (
                        <div className="flex items-center gap-2 mt-3 px-1">
                          {socialLinks.website && (
                            <a
                              href={socialLinks.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Website"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="2" y1="12" x2="22" y2="12"/>
                                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.twitter && (
                            <a
                              href={socialLinks.twitter}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="X (Twitter)"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.instagram && (
                            <a
                              href={socialLinks.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Instagram"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                              </svg>
                            </a>
                          )}
                          {socialLinks.discord && (
                            <a
                              href={socialLinks.discord}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                              title="Discord"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                              </svg>
                            </a>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Right side - Details */}
              <div className="w-1/2 p-6 border-l border-[#2a2a2a] overflow-y-auto">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-white">
                        {pieceDetailModal.item?.name}
                      </h2>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(pieceDetailModal.item);
                        }}
                        className="p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                        title={isFavorited(pieceDetailModal.item?.id) ? "Remove from favorites" : "Add to favorites"}
                      >
                        <Heart 
                          size={20} 
                          className={`transition-all duration-300 ${
                            isFavorited(pieceDetailModal.item?.id)
                              ? 'text-red-500 fill-red-500'
                              : 'text-gray-400 hover:text-red-500'
                          }`} 
                        />
                      </button>
                    </div>
                    {/* Custom Tags */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {pieceDetailModal.item?.tags && pieceDetailModal.item.tags.length > 0 ? (
                        pieceDetailModal.item.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">No tags</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setActivityPanelOpen(!activityPanelOpen)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      activityPanelOpen 
                        ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' 
                        : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a] hover:text-white border border-[#2a2a2a]'
                    }`}
                  >
                    Activity
                  </button>
                </div>

                <div className="space-y-5">
                  {/* Category and Color */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        <span className="text-gray-300 text-sm font-medium">
                          {pieceDetailModal.item?.category || 'Uncategorized'}
                        </span>
                      </div>
                      {pieceDetailModal.item?.color && (
                        <div className="text-gray-300 text-sm">
                          <span className="text-gray-500">Color: </span>
                          <span className="capitalize font-medium">{pieceDetailModal.item.color}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <h3 className="text-white font-semibold mb-2 text-sm">Description</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {pieceDetailModal.item?.description || 'A premium digital fashion piece with modern styling and high-quality design.'}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <h3 className="text-white font-semibold mb-3 text-sm">Pricing</h3>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Price</span>
                        <div className="text-green-400 font-bold text-xl">
                          {(() => {
                            const item = pieceDetailModal.item;
                            const displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                            return displayPrice;
                          })()} SOL
                        </div>
                      </div>
                      <div className="text-right text-gray-500 text-sm">
                        ≈ ${(() => {
                          const item = pieceDetailModal.item;
                          const displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                          return ((displayPrice || 0) * solPrice).toFixed(2);
                        })()} USD
                      </div>
                    </div>
                  </div>

                  {/* Scarcity */}
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                    <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                    <div className="text-white font-semibold text-lg">
                      {pieceDetailModal.item?.quantity || 1}
                    </div>
                  </div>

                  {/* Dropped Date */}
                  <div className="text-gray-500 text-sm pt-2">
                    Dropped {pieceDetailModal.item?.createdAt ? new Date(pieceDetailModal.item.createdAt).toLocaleDateString('en-US', { 
                      year: '2-digit', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : 'Recently'}
                  </div>

                  {/* Action Buttons */}
                  {(() => {
                    if (isDraft) {
                      return (
                        <div className="w-full px-4 py-3 bg-gray-800 text-gray-500 rounded-lg text-center">
                          <span className="text-sm">Draft Piece - Not Available for Purchase</span>
                        </div>
                      );
                    }

                    // Check if current user owns this piece
                    const isOwner = isConnected && publicKey && 
                      pieceDetailModal.item?.holder_wallet === publicKey.toString();
                    
                    // Determine if piece is owned by creator (primary sale)
                    const creatorWallet = pieceDetailModal.item?.creator_wallet || 
                                        pieceDetailModal.item?.creators?.wallet ||
                                        pieceDetailModal.item?.originalCreator?.wallet;
                    const isOwnedByCreator = pieceDetailModal.item?.holder_wallet === creatorWallet;
                    
                    // Show buy button only if:
                    // 1. User doesn't own it, AND
                    // 2. (Piece is owned by creator OR piece is listed for resale)
                    const canBuy = !isOwner && (isOwnedByCreator || pieceDetailModal.item?.listing_status === 'listed');
                    
                    // Show trade button only if user doesn't own it
                    const canTrade = !isOwner;

                    // If user owns the piece, show different message
                    if (isOwner) {
                      return (
                        <div className="w-full px-4 py-3 bg-green-600/20 text-green-400 rounded-lg text-center border border-green-600/30">
                          <span className="text-sm">✓ You own this piece</span>
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-3 pt-4 pb-2">
                        {canBuy && (
                          <button 
                            onClick={() => handleBuyNow(pieceDetailModal.item)}
                            className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25"
                          >
                            Buy Now - {(() => {
                              const item = pieceDetailModal.item;
                              const displayPrice = item?.lowestPrice ?? item?.listing_price ?? item?.price;
                              return displayPrice;
                            })()} SOL
                          </button>
                        )}
                        <div className="flex gap-3">
                          <button 
                            onClick={() => handleMakeOffer(pieceDetailModal.item)}
                            className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a]"
                          >
                            Make Offer
                          </button>
                          {canTrade && (
                            <button 
                              onClick={() => handleRequestTrade(pieceDetailModal.item)}
                              className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a] flex items-center justify-center gap-2"
                            >
                              <ArrowLeftRight size={16} />
                              Request Trade
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Activity Side Panel - slides out from behind modal on desktop, overlay on mobile */}
            <div 
              className="absolute top-0 left-0 md:left-full h-full w-full md:w-80 bg-[#0a0a0a] border-l border-[#2a2a2a] md:rounded-r-2xl transition-all duration-300 ease-in-out shadow-2xl"
              style={{ 
                transform: activityPanelOpen ? 'translateX(0)' : (window.innerWidth < 768 ? 'translateX(-100%)' : 'translateX(-100%)'),
                zIndex: activityPanelOpen ? 50 : 0,
                opacity: activityPanelOpen ? 1 : 0,
                visibility: activityPanelOpen ? 'visible' : 'hidden',
                pointerEvents: activityPanelOpen ? 'auto' : 'none'
              }}
            >
              <div className="h-full flex flex-col p-5">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#2a2a2a]">
                  <h3 className="text-base font-bold text-white">Activity</h3>
                  <button
                    onClick={() => setActivityPanelOpen(false)}
                    className="p-1.5 hover:bg-[#1a1a1a] rounded-lg transition-colors"
                    title="Close Activity Panel"
                  >
                    <ChevronRight size={18} className="text-gray-400" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <PieceActivityAndHolders
                    pieceId={pieceDetailModal.item?.id}
                    pieceName={pieceDetailModal.item?.name}
                    basePrice={parseFloat(pieceDetailModal.item?.price) || 0}
                    solPrice={solPrice || 150}
                    totalQuantity={pieceDetailModal.item?.total_quantity || pieceDetailModal.item?.quantity || 1}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Buy Modal */}
      {buyModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Purchase Item</h3>
                <p className="text-gray-400 text-sm">Complete your purchase</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-2">{buyModal.item?.name}</h4>
                {(() => {
                  const itemPrice = parseFloat(buyModal.item?.listing_price || buyModal.item?.price || '0');
                  if (!itemPrice || itemPrice <= 0) {
                    return (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-2">
                        <p className="text-red-400 text-sm">
                          ⚠️ This piece has no price set. Cannot purchase.
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Price</span>
                        <div className="text-right">
                          <div className="text-green-400 font-bold text-lg">{itemPrice.toFixed(4)} SOL</div>
                          <div className="text-gray-500 text-sm">
                            ≈ ${(itemPrice * solPrice).toFixed(2)} USD
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Sale Fee (2.5%)</span>
                        <div className="text-right">
                          <div className="text-orange-400 font-medium">
                            {(itemPrice * 0.025).toFixed(4)} SOL
                          </div>
                          <div className="text-gray-500 text-sm">
                            ≈ ${((itemPrice * 0.025) * solPrice).toFixed(2)} USD
                          </div>
                        </div>
                      </div>
                      <div className="border-t border-gray-700 pt-2">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-300 text-sm font-medium">Seller Receives</span>
                          <div className="text-right">
                            <div className="text-white font-medium">
                              {(itemPrice * 0.975).toFixed(4)} SOL
                            </div>
                            <div className="text-gray-500 text-sm">
                              ≈ ${((itemPrice * 0.975) * solPrice).toFixed(2)} USD
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Wallet Info */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-3">Wallet Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Connected Wallet</span>
                    <div className="flex items-center gap-2">
                      <Wallet size={16} className="text-green-400" />
                      <span className="text-green-400 text-sm font-medium">
                        {isConnected ? 'Connected' : 'Not Connected'}
                      </span>
                    </div>
                  </div>
                  {isConnected && publicKey && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Address</span>
                      <span className="text-white text-sm font-mono">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                    </div>
                  )}
                  {isConnected && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Balance</span>
                      <div className="text-right">
                        <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-xs">
                          ≈ ${(balance * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-gray-300 text-sm">
                You are about to purchase this digital fashion piece. The transaction will be processed on the Solana blockchain.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={closeBuyModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={processPurchase}
                disabled={isProcessing || !isConnected || balance < parseFloat(buyModal.item?.listing_price || buyModal.item?.price || '0')}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Wallet size={16} />
                    Confirm Purchase
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offer Modal */}
      {offerModal.isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Package size={24} className="text-blue-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Make an Offer</h3>
                <p className="text-gray-400 text-sm">Submit your offer for this piece</p>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-2">{offerModal.item?.name}</h4>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">List Price</span>
                  <div className="text-right">
                    <div className="text-white font-bold text-lg">{offerModal.item?.price || '2.5 SOL'}</div>
                    <div className="text-gray-500 text-sm">
                      ≈ ${(parseFloat(offerModal.item?.price || '2.5') * solPrice).toFixed(2)} USD
                    </div>
                  </div>
                </div>
              </div>

              {/* Offer Amount Input */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <label className="block text-white font-medium mb-3">Your Offer Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={offerModal.offerAmount}
                    onChange={(e) => setOfferModal(prev => ({ ...prev, offerAmount: e.target.value }))}
                    placeholder="0.00"
                    className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                    SOL
                  </div>
                </div>
                {offerModal.offerAmount && !isNaN(parseFloat(offerModal.offerAmount)) && (
                  <div className="mt-2 text-right text-gray-500 text-sm">
                    ≈ ${(parseFloat(offerModal.offerAmount) * solPrice).toFixed(2)} USD
                  </div>
                )}
              </div>

              {/* Wallet Info */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <h4 className="text-white font-medium mb-3">Wallet Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Connected Wallet</span>
                    <div className="flex items-center gap-2">
                      <Wallet size={16} className="text-green-400" />
                      <span className="text-green-400 text-sm font-medium">
                        {isConnected ? 'Connected' : 'Not Connected'}
                      </span>
                    </div>
                  </div>
                  {isConnected && publicKey && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Address</span>
                      <span className="text-white text-sm font-mono">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                    </div>
                  )}
                  {isConnected && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Balance</span>
                      <div className="text-right">
                        <div className="text-white font-medium">{balance.toFixed(4)} SOL</div>
                        <div className="text-gray-500 text-xs">
                          ≈ ${(balance * solPrice).toFixed(2)} USD
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-gray-300 text-sm">
                Your offer will be locked in escrow until the seller accepts or rejects it. You can cancel your offer at any time.
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={closeOfferModal}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={processOffer}
                disabled={isProcessing || !isConnected || !offerModal.offerAmount || isNaN(parseFloat(offerModal.offerAmount)) || parseFloat(offerModal.offerAmount) <= 0 || balance < parseFloat(offerModal.offerAmount || '0')}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Package size={16} />
                    Submit Offer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className={`${
            notification.type === 'success' ? 'bg-green-500' : 
            notification.type === 'error' ? 'bg-red-500' : 
            'bg-blue-500'
          } text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2`}>
            {notification.type === 'success' && <span>✓</span>}
            {notification.type === 'error' && <span>✕</span>}
            <span>{notification.message}</span>
          </div>
        </div>
      )}

      {/* Grouped Pieces Modal */}
      <GroupedPiecesModal
        isOpen={groupedPiecesModal.isOpen}
        onClose={() => setGroupedPiecesModal({ isOpen: false, groupedCard: null })}
        groupedCard={groupedPiecesModal.groupedCard}
        solPrice={solPrice}
        wallet={wallet}
        publicKey={publicKey}
        isConnected={isConnected}
        onBuyNow={(selectedPiece) => {
          // Trigger buy flow for selected piece
          handleBuyPiece(selectedPiece);
          setGroupedPiecesModal({ isOpen: false, groupedCard: null });
        }}
        onMakeOffer={(selectedPiece) => {
          // Trigger offer flow for selected piece
          handleMakeOffer(selectedPiece);
          setGroupedPiecesModal({ isOpen: false, groupedCard: null });
        }}
        onRequestTrade={(selectedPiece) => {
          // Trigger trade flow for selected piece
          handleRequestTrade(selectedPiece);
          setGroupedPiecesModal({ isOpen: false, groupedCard: null });
        }}
      />

      {/* Request Trade Modal */}
      <RequestTradeModal
        isOpen={requestTradeModal.isOpen}
        onClose={() => setRequestTradeModal({ isOpen: false, item: null })}
        targetPiece={requestTradeModal.item}
        onRequestTrade={handleTradeRequestSubmit}
        onSuccess={handleTradeRequestSuccess}
      />
    </div>
  );
};

export default CapsulePage;
